/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React from 'react';
import { cssMap } from '@compiled/react';
import { useBlueprintsTourDismissed } from '@jira/polaris__lib-onboarding-flows/src/controllers/selectors/blueprints-hooks.tsx';
import Lozenge from '@atlaskit/lozenge/lozenge';
import TemplateIcon from '@atlaskit/icon-lab/core/template';
import ButtonItem from '@atlaskit/menu/button-item';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { ViewBlueprintsModalTrigger } from './ViewBlueprintsModalTrigger.tsx';

const sampleProjectButtonLabel = defineMessage({
	id: 'polaris-component-view-blueprints.trigger-button.text',
	defaultMessage: 'Try pre-built views',
	description: 'Label of the view templates modal trigger menu button',
});

const sampleProjectButtonLabelTried = defineMessage({
	id: 'polaris-component-view-blueprints.trigger-button.text-tried',
	defaultMessage: 'Pre-built views',
	description: 'Label of the view templates modal trigger menu button',
});

const newLozengeLabel = defineMessage({
	id: 'polaris-component-view-blueprints.trigger-button.new-lozenge',
	defaultMessage: 'New',
	description:
		'The text is shown inside a lozenge badge displayed next to the view templates trigger button, indicating that the feature is newly available.',
});

const buttonLabelMessage = defineMessage({
	id: 'polaris-component-view-blueprints.trigger-button.label',
	defaultMessage: 'Create sample view templates project',
	description:
		'The text is shown as the accessible label for the button that triggers creation of a sample view templates project in a project-based workspace.',
});

const buttonLabelGalaxia = defineMessage({
	id: 'polaris-component-view-blueprints.trigger-button.label-galaxia',
	defaultMessage: 'Create sample view templates space',
	description:
		'The text is shown as the accessible label for the button that triggers creation of a sample view templates space in a space-based (Galaxia) workspace.',
});

type ViewBlueprintsTriggerProps = {
	children: (props: { ref: React.Ref<HTMLElement> }) => React.ReactNode;
};

type ViewBlueprintsTriggerButtonProps = {
	noIcon?: boolean;
	label?: string;
};

/**
 * ViewBlueprintsTrigger - A wrapper component that provides modal trigger functionality
 * This allows rendering the trigger as different elements in various components
 */
export const ViewBlueprintsTrigger = ({
	children,
}: ViewBlueprintsTriggerProps): React.JSX.Element => {
	return <ViewBlueprintsModalTrigger>{children}</ViewBlueprintsModalTrigger>;
};

export const ViewBlueprintsTriggerButton = ({
	noIcon,
	label,
	...rest
}: ViewBlueprintsTriggerButtonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const tourDismissed = useBlueprintsTourDismissed();
	// If tour is dismissed, the user must have created at least one blueprint
	const userTriedBlueprints = tourDismissed === true;

	const iconBefore = noIcon ? undefined : (
		<TemplateIcon
			spacing="spacious"
			color="currentColor"
			label={formatMessage(
				expVal('project-terminology-refresh', 'isEnabled', false)
					? buttonLabelGalaxia
					: buttonLabelMessage,
			)}
		/>
	);

	const buttonLabel =
		label ||
		formatMessage(userTriedBlueprints ? sampleProjectButtonLabelTried : sampleProjectButtonLabel);

	return (
		<ViewBlueprintsTrigger>
			{({ ref }) => (
				<ButtonItem {...rest} ref={ref} iconBefore={iconBefore}>
					<Inline>
						<Box>{buttonLabel}</Box>
						<Box xcss={styles.lozengeWrapper} as="div">
							{!userTriedBlueprints && (
								<Lozenge
									appearance={
										fg('platform-dst-lozenge-tag-badge-visual-uplifts') ? 'discovery' : 'new'
									}
								>
									{formatMessage(newLozengeLabel)}
								</Lozenge>
							)}
						</Box>
					</Inline>
				</ButtonItem>
			)}
		</ViewBlueprintsTrigger>
	);
};

const styles = cssMap({
	lozengeWrapper: { paddingInlineStart: token('space.100') },
});
